import { css } from '@emotion/react';
import { CSSProperties } from 'react';
import { Card } from '../../../components/Card/Card';
import { KPI } from '../../../components/Card/KPI';
import { ContainerName } from '../../../constants/styles';
import { ICaptableDataModel } from '../../../data-models/captable2.data-model';
import { FMT } from '../../../util/formatter-service';
import { KpiConfig, useCaptableKpiSummary } from './useCaptableKpiSummary';

const centeredKpiStyle = css({
  textAlign: 'center',
  '.kpi-card > *': {
    justifyContent: 'center',
  },
});

const metricsCardStyle = css`
  & > div {
    background: radial-gradient(
      100% 204.54% at 0% 0%,
      rgba(234, 244, 251, 0.8) 0%,
      rgba(200, 209, 249, 0.32) 100%
    );
  }
`;

/** Card gap, and the width a card wraps at once the rows stop being laid out in columns. Both are
 *  rem, as is every width derived from them below — a px breakpoint would stop matching the card it
 *  was sized for as soon as the reader raises their browser's font size. */
const CardGapRem = 0.5;
const MinCardRem = 13.125;

/**
 * Narrowest card that renders the longest label in use in full: 'Last Post-Money Valuation', 224px
 * against the shipped Wotfard face, which three tenant-renamable columns can reach. Only the
 * single-row threshold is sized from it, so that collapsing to one row is never what introduces an
 * ellipsis — a wider window showing less would read as a bug. Narrower rows stay on {@link
 * MinCardRem} and let a long label fall back to the title attribute KPI already sets.
 */
const NoClipCardRem = 14;

/** Container holding `--kpi-columns` cards across, the design's 1024px viewport less the page chrome. */
const MultiRowMinRem = 54.5;

/** How many one-row bands to emit. The summary tops out at seven cards today; the eighth is
 *  headroom, so adding a card does not silently cost it its single row. */
const MaxCards = 8;

const rem = (value: number) => `${value}rem`;

/** Container that fits `count` cards on one row, each at its no-clip width. */
const oneRowMin = (count: number) => rem(count * NoClipCardRem + (count - 1) * CardGapRem);

/**
 * Flex rather than grid so a row fills the container: a wrapped line shares its own free space, so a
 * trailing row of three is simply wider than a leading row of four. A fixed-track grid would leave
 * the last row short by whatever the card count owes the track count.
 *
 * Cards only grow once the container is wide enough to lay them out in known columns. Below that
 * they keep their width and the last row is short, rather than one leftover card stretching across
 * the whole row.
 */
const cardContainer = css`
  display: flex;
  flex-wrap: wrap;
  gap: ${rem(CardGapRem)};
  padding: 0.25rem 0 0.75rem;
  transition: opacity 0.2s ease;

  &.loading {
    opacity: 0.6;
  }

  > * {
    flex: 0 1 ${rem(MinCardRem)};
    // Lets a card shrink past its own label. Without it a container narrowed by both side panels
    // overflows into CPWrapper's clipped overflow-x, hiding cards with no scrollbar to reveal them.
    min-width: 0;
  }

  @container ${ContainerName.captableContainer} (width >= ${rem(MultiRowMinRem)}) {
    > * {
      flex-grow: 1;
      flex-basis: calc((100% - (var(--kpi-columns) - 1) * ${rem(CardGapRem)}) / var(--kpi-columns));
    }
  }

  // One row as soon as every card fits at its no-clip width. That width depends on how many cards
  // there are and a container query cannot read a custom property, so each count gets its own band.
  ${Array.from(
    { length: MaxCards },
    (_, i) => `
      @container ${ContainerName.captableContainer} (width >= ${oneRowMin(i + 1)}) {
        &[data-kpi-count='${i + 1}'] > * {
          flex-grow: 1;
          flex-basis: 0;
        }
      }
    `
  ).join('')}
`;

interface ICaptableKpiSummaryProps {
  captable: ICaptableDataModel;
}

function KpiCard({ config }: { config: KpiConfig }) {
  const isMetricsSource = config.source === 'metrics';

  return (
    <div css={[centeredKpiStyle, isMetricsSource && metricsCardStyle]}>
      <Card>
        <KPI
          title={config.title}
          value={FMT.format(config.format, config.value)}
          indicator={null}
          description={config.description}
        />
      </Card>
    </div>
  );
}

/**
 * Cards per row once the container is wide enough to grow them but too narrow for a single row.
 * Halving splits the seven-card portco into the 4 + 3 the design asks for and the six-card one into
 * an even 3 + 3; four cards or fewer already fit across.
 */
export function columnsForCardCount(count: number): number {
  return count <= 4 ? Math.max(1, count) : Math.ceil(count / 2);
}

interface ICaptableKpiSummaryViewProps {
  kpiConfigs: KpiConfig[];
  isLoading: boolean;
}

export function CaptableKpiSummaryView({ kpiConfigs, isLoading }: ICaptableKpiSummaryViewProps) {
  const columns = { '--kpi-columns': columnsForCardCount(kpiConfigs.length) } as CSSProperties;

  return (
    <div
      css={cardContainer}
      className={isLoading ? 'loading' : ''}
      style={columns}
      data-kpi-count={kpiConfigs.length}
      data-testid={'captable-kpi-summary'}
    >
      {kpiConfigs.map((config) => (
        <KpiCard key={config.key} config={config} />
      ))}
    </div>
  );
}

export function CaptableKpiSummary({ captable }: ICaptableKpiSummaryProps) {
  const { kpiConfigs, isLoading } = useCaptableKpiSummary(captable);

  return <CaptableKpiSummaryView kpiConfigs={kpiConfigs} isLoading={isLoading} />;
}
